import {extend} from 'vee-validate';
import i18n from '@/language/i18n';
import isObject from 'lodash/isObject';
import uniq from 'lodash/uniq';
import REGEX from '@/validation/enum/REGEX';

// default rules
import {max_value} from "vee-validate/dist/rules";

extend('maxStringLength', {
  params: ['target'],
  validate(value, { target }) {
    return (
      target === '' ||
      target === undefined ||
      target === null ||
      value === '' ||
      value === undefined ||
      value === null ||
      value.length <= target
    );
  },
  computesRequired: false,
  message: (fieldName, params) =>
    i18n.t('Validations.maxStringLength', [params.target]),
});

extend('maxAmount', {
  ...max_value,
  message: (fieldName, placeholders) => {
    return i18n.t('Validations.amountHigherThanStock', [placeholders.max]);
  }
});

extend('amountHigherThanMax', {
  ...max_value,
  message: (fieldName, placeholders) => {
    return i18n.t('Validations.amountHigherThanMax', [placeholders.max]);
  }
});

extend('maxMoveable', {
  ...max_value,
  message: (fieldName, placeholders) => {
    return i18n.t('Validations.amountHigherThanRelocatableAmount', [placeholders.max]);
  }
});

const exists = (value, excludeItems) => {
  let items = excludeItems.length > 0 ? excludeItems : [null, undefined, ''];
  if (isObject(value)) {
    return Object.keys(value).length > 0
  }
  else return !items.includes(value);
}

extend('required', {
  validate: exists,
  computesRequired: true,
  message: i18n.t('Validations.required')
});

extend('requiredUsername', {
  validate: exists,
  computesRequired: true,
  message: i18n.t('Validations.usernameRequired')
});

extend('passwordRequired', {
  validate: exists,
  computesRequired: true,
  message: i18n.t('Validations.passwordRequired')
});

extend('dateGoE', {
  params: ['target'],
  validate(value, { target }) {
    return target === '' ||
        target === undefined ||
        target === null ||
        value === '' ||
        value === undefined ||
        value === null ||
        value >= target;
  },
  computesRequired: false,
  message: (fieldName, params) =>
    i18n.t('Validations.xCannotBeBeforeY', [fieldName, params.target])
});

extend('confirmed', {
  params: ['target'],
  validate(value, { target }) {
    return ((target === '' || target === undefined) && value === '') ||
        value === target;
  },
  computesRequired: true,
  message: i18n.t('Validations.inputsNotIdentical')
});

extend('greaterThanZero', {
  validate(value) {
    return value > 0;
  },
  message: i18n.t('Validations.valueMustBeBiggerThanZero')
});

extend('discountExceedsPrice', {
  params: ['singlePrice', 'amount'],
  validate(value, { singlePrice, amount }) {
    if (singlePrice &&  amount) {
      const totalPrice = singlePrice * amount;
      return Number(totalPrice) >= value;
    }
    return true;
  },
  message: i18n.t('Validations.discountExceedsPrice')
});

extend('greaterThanOrEqualsZero', {
  validate(value) {
    return value >= 0;
  },
  message: i18n.t('Validations.valueMustBeBiggerThanZero')
});

extend('exact100%', {
  validate(value) {
    return value === 1;
  },
  message: i18n.t('Validations.valueMustBeOneHundredPercent')
});

extend('noSelfDeputy', {
  params: ['target'],
  validate(value, { target }) {
    return value != target;
  },
  message: i18n.t('Validations.employeeCannotBeHisOwnDeputy')
});

extend('email', {
  validate(value) {
    return REGEX.EMAIL.test(value);
  },
  message: i18n.t('Validations.invalidEmail')
});

extend('time', {
  validate(value) {
    return REGEX.TIME.test(value);
  },
  message: i18n.t('Validations.invalidFormat')
});

extend('workingTimeBreak', {
  params: ['breakConfiguration', 'breakLength'],
  validate(value, { breakConfiguration, breakLength }) {
    let desiredBreak = parseInt(breakConfiguration);
    let actualBreak = parseInt(breakLength)
    if (!isNaN(actualBreak) && actualBreak >= 0 && !isNaN(desiredBreak)) return actualBreak >= desiredBreak;
    return true;
  },
  message: (fieldName, params) => i18n.t('Validations.minimumPause', [params.breakConfiguration])
});

extend('workingTimeGrid', {
  params: ['grid', 'workingTime'],
  validate(value, { grid, workingTime }) {
    let working = workingTime ? workingTime : value;
    if (working) {
      let minutes = working.split(':')[1];
      return minutes % grid === 0;
    }
    return true;
  },
  message: (fieldName, params) => i18n.t('Validations.minuteGrid', [params.grid])
});

extend('minWorkingTime', {
  params: ['workingTimeInMinutes', 'minWorkingTime'],
  validate(value, { workingTimeInMinutes, minWorkingTime }) {
    return parseInt(workingTimeInMinutes) > parseInt(minWorkingTime);
  },
  message: i18n.t('Validations.undershotMinimumWorkingTime')
});

extend('regex', {
  params: ['regex'],
  validate(value, { regex }) {
    if (regex == 'undefined' || regex == 'null') return true;

    let stringRegex = regex;
    if (typeof(regex) == 'object') stringRegex = regex.toString();

    var formattedRegex = ('^' + stringRegex + '$');
    const convertRegex = new RegExp(formattedRegex, "i")
    return convertRegex.test(value);
  },
  message: (fieldname) => i18n.t('Validations.doesNotMatchSpecification', [fieldname])
});

extend('passwordRegex', {
  params: ['regex'],
  validate(value, { regex }) {
    if (regex == 'undefined' || regex == 'null') return true;

    let stringRegex = regex;
    if (typeof(regex) == 'object') stringRegex = regex.toString();

    var formattedRegex =
      (!stringRegex.startsWith('^') && !stringRegex.endsWith('$'))  
      ? ('^' + stringRegex + '$')
      : stringRegex;

    const convertRegex = new RegExp(formattedRegex)
    return convertRegex.test(value);
  },
  message: (fieldname) => i18n.t('Validations.doesNotMatchSpecification', [fieldname])
});

extend('number', {
  validate(value) {
    return !isNaN(value);
  },
  message: i18n.t('Validations.inputNumber')
});

extend('positiveNumber', {
  validate(value) {
    return value >= 0;
  },
  message: i18n.t('Validations.inputPositiveNumber')
});

extend('phoneNumber', {
  validate(value) {
    return REGEX.PHONE.test(value);
  },
  message: i18n.t('Validations.inputTelephoneNumber')
});

extend('oneOf3', {
  params: ['other1', 'other2'],
  validate(value, { other1, other2 }) {
    return [value, other1, other2].some(exists);
  },
  computesRequired: true,
  message: (fieldName, params) =>
    i18n.t('Validations.fillOutXYOrZ', [fieldName, params.other1, params.other2])
});

extend('oneOf2', {
  params: ['other'],
  validate(value, { other }) {
    return [value, other].some(exists);
  },
  computesRequired: true,
  message: (fieldName, params) =>
    i18n.t('Validations.fillOutXOrY', [fieldName, params.other])
});

extend('atLeastOne', {
    validate(value, args) {
      return [value].some(v => exists(v, [undefined, ''])) || args.some(v => exists(v, [undefined, '']));
    },
    computesRequired: true,
    message: i18n.t('Validations.inputAtLeastOneDestination')
  }
);


extend('atLeastOneDeliveryCalculationItem', {
  params: ['item'],
  validate(value, { item }) {
    return item.length > 0;
  },
  computesRequired: true,
  message: i18n.t('Validations.inputAtLeastOneItem')
}
);

extend('onlyAssignedOnce', {
  params: ['other'],
  validate(value, { other }) {
    const duplicateFree = uniq(other);
    return duplicateFree.length === other.length;
  },
  message: () => `Jeder Mitarbeiter darf nur einmal zugeordnet werden`
});

extend('roleOnlyAssignedOnce', {
  params: ['other'],
  validate(value, { other }) {
    const duplicateFree = uniq(other);
    return duplicateFree.length === other.length;
  },
  message: () => `Jeder Rolle darf nur einmal zugeordnet werden`
});

extend('contactRequired', {
  params: ['other'],
  validate(value, { other }) {
    if (value > 1 && value <= 4) {
      if (other == undefined) return false;
    }
    return true;
  },
  computesRequired: true,
  message: `Bitte legen Sie zunächst einen Kontakt an!`
});

extend('projectRequired', {
  params: ['other'],
  validate(value, { other }) {
    if (value == 4) {
      if (other == undefined) return false;
    }
    return true;
  },
  computesRequired: true,
  message: `Bitte zunächst eine Projektverknüpfung erstellen!`
});

extend('cancelReasonRequired', {
  params: ['other1', 'other2'],
  validate(value, { other1, other2 }) {
    if (value == 7) {
      if ((other1 != undefined) ^ (other2 != undefined)) return true;
      else return false;
    }
    return true;
  },
  computesRequired: true,
  message: `Bitte wählen Sie zunächst einen Grund für die Absage an!`
});

extend('endIsAfterStart', {
  params: ['other1'],
  validate(value, { other1 }) {
    return other1 === 'true';
  },
  message: `Ungültige Zeit`
});

extend('dayInPastOrToday', {
  validate(value) {
    return new Date().getTime() > new Date(value).getTime();
  },
  computesRequired: true,
  message: `Der Tag darf nicht in der Zukunft liegen.`
});

extend('yearInPastOrToday', {
  validate(value) {
    return new Date().getFullYear() >= value;
  },
  computesRequired: false,
  message: i18n.t('Validations.yearInPastOrToday')
});

extend('yearIsAfter', {
  params: ['minYear'],
  validate(value, { minYear }) {
    return value >= parseInt(minYear);
  },
    computesRequired: false,
    message: (fieldName, params) => i18n.t('Validations.yearIsAfter', [params.minYear])
  });

extend('stockNotNegative', {
  params: ['maxAmountOfStock'],
  validate(value, { maxAmountOfStock }) {
    if (value && maxAmountOfStock != 'undefined') {
      return parseFloat(value) <= parseFloat(maxAmountOfStock)
    } else if (maxAmountOfStock === 'undefined') return false
    return true;
  },
  message: `Menge nicht vorrätig`
});

extend('dividableByBaseFactor', {
  params: ['productUnitFactor'],
  validate(value, { productUnitFactor }) {
    return ((value * productUnitFactor) % productUnitFactor) === 0;
  },
  message: `Ungültige Menge`
});

extend('canBeRemovedFromSource', {
  params: ['canBeRemovedFromSource'],
  validate(value, { canBeRemovedFromSource }) {
    return canBeRemovedFromSource === 'true';
  },
  message: `Gewählte Lagerbewegung ist nicht möglich`
});

extend('isSourceAmountEqualTargetAmount', {
  params: ['sumOfSourceInBase', 'sumOfTargetInBase'],
  validate(value, { sumOfSourceInBase, sumOfTargetInBase }) {
    return sumOfSourceInBase === sumOfTargetInBase;
  },
  message: `Mengen stimmen nicht überein`
});

extend('earliestStart', {
  params: ['earliestStart', 'earliestStartInMinutes', 'startOfWorkingTime'],
  validate(value, { earliestStartInMinutes, startOfWorkingTime }) {
    return parseInt(startOfWorkingTime) >= parseInt(earliestStartInMinutes);
  },
  message: (fieldName, params) => `${i18n.t('Validations.earliestWorkStart')} ${params.earliestStart}`
});

extend('latestEnd', {
  params: ['latestEnd', 'latestEndInMinutes', 'endOfWorkingTime'],
  validate(value, { latestEndInMinutes, endOfWorkingTime }) {
    return parseInt(endOfWorkingTime) <= parseInt(latestEndInMinutes);
  },
  message: (fieldName, params) => `${i18n.t('Validations.latestWorkEnd')} ${params.latestEnd}`
});

extend('valueLessThan', {
  params: ['val', 'max'],
  validate(value, { val, max }) {
    return parseFloat(val) <= parseFloat(max);
  },
  message: `Zu viele Projektstunden gebucht.`
});


extend('amountInBaseIsGreater', {
  params: ['minimalAmount', 'amount'],
  validate(value, { minimalAmount, amount }) {
    return parseInt(minimalAmount) <= parseInt(amount);
  },
  message: (fieldName) => i18n.t('Validations.mustBeGreaterThanMinimumStock', [fieldName])
});

extend('amountIsGreater', {
  params: ['minimalAmount', 'amount'],
  validate(value, { minimalAmount }) {
    return parseFloat(minimalAmount) <= parseFloat(value);
  },
  message: (fieldName) => i18n.t('Validations.mustBeGreaterThanMinimumOrderQuantity', [fieldName])
});

extend('amountIsGreaterIfBothAreFilled', {
  params: ['minimalAmount', 'amount'],
  validate(value, { minimalAmount }) {
    if (!minimalAmount) {
      return true;
    }
    return parseFloat(minimalAmount) <= parseFloat(value);
  },
  message: (fieldName) => i18n.t('Validations.mustBeGreaterThanMinimumOrderQuantity', [fieldName])
});

extend('isLess', {
  params: ['maxAmount'],
  validate(value, { maxAmount }) {
    if (!maxAmount) return true;
    return parseFloat(value) <= parseFloat(maxAmount);
  },
  message: (fieldName, params) => i18n.t('Validations.xMustBeSmallerThanY', [fieldName, params.maxAmount])
});

extend('isGreater', {
  params: ['minAmount'],
  validate(value, { minAmount }) {
    if (!minAmount) return true;
    return parseFloat(minAmount) <= parseFloat(value);
  },
  message: (fieldName, params) => i18n.t('Validations.xMustBeBiggerThanY', [fieldName, params.minAmount])
});

extend('isBetween', {
  params: ['min', 'max'],
  validate(value, { min, max }) {
    return parseFloat(min) <= parseFloat(value)
      && parseFloat(value) <= parseFloat(max);
  },
  message: (fieldName, params) => i18n.t('Validations.xMustBeBetweenYAndZ', [fieldName, params.min, params.max])
});

extend('convertableUnits', {
  params: ['unit'],
  validate(value, { unit }) {
    if (value?.id == unit?.id || value == undefined || unit == undefined) return true;
    if (value.data?.baseProductUnit == undefined)
      return value.data?.label == unit?.data?.baseProductUnit
    if (unit.data?.baseProductUnit == undefined)
      return unit.data?.label == value.data?.baseProductUnit
    return value.data?.baseProductUnit == unit.data?.baseProductUnit
  },
  message: i18n.t('Validations.incompatibleUnits')
});

extend('greater8Chars', {
  validate(value) {
    let passwordLength = String(value).length;
    return passwordLength >= 8;
  },
  message: i18n.t('Validations.mustAtLeastContainEightCharacters')
});

extend('atLeast4DifferentTypeOfChars', {
  validate(value) {
    let password = String(value);

    let hasNumerics = /\d+/.test(password);
    let hasCapitals = /[A-Z]+/.test(password);
    let hasSmalls = /[a-z]+/.test(password);
    let hasSepcials = /[!@#$%&\\/=\\?_\\.,:;\\-]+/.test(password);

    return hasNumerics && hasCapitals && hasSmalls && hasSepcials;
  },
  message: i18n.t('Validations.mustContainFourDifferentTypesOfCharacters')
});

extend('sameYear', {
  params: ['target'],
  validate(value, { target }) {
    return target === '' ||
        target === undefined ||
        value === '' ||
        value === undefined ||
        value.substring(0, 4) === target.substring(0, 4);
  },
  computesRequired: true,
  message: (fieldName, params) =>
    i18n.t('Validations.xMustBeInTheSameYearAsY', [fieldName, params.target])
});

extend('todayOrLater', {
  validate(value) {
    return value === '' ||
      value === undefined ||
      value >= new Date().toISOString().split('T')[0];
  },
  message: i18n.t('Validations.dateCannotBeInThePast')
});

extend('confirmationRequired', {
  validate(value) {
    return value
  },
  computesRequired: true,
  message: i18n.t('Validations.confirmThatMessageWasRead')
});

extend('isNotRootItem', {
  params: ['isRoot'],
  validate(value, { isRoot }) {
    return (isRoot != undefined && isRoot==='true' )
  },
  computesRequired: true,
  message: i18n.t('Validations.attributeIsNoRootItem')
});

extend('maxStringLengthWithoutHtml', {
  params: ['textLengthWithoutHtml', 'characterLimit'],
  validate(value, { textLengthWithoutHtml, characterLimit }) {
    return (
      characterLimit === '' ||
      characterLimit === undefined ||
      characterLimit === null ||
      textLengthWithoutHtml === '' ||
      textLengthWithoutHtml === undefined ||
      textLengthWithoutHtml === null ||
      value === '' ||
      value === undefined ||
      value === null ||
      Number(textLengthWithoutHtml) <= Number(characterLimit)
    );
  },
  computesRequired: false,
  message: (fieldName, params) => {
    return i18n.t('Validations.maxStringLength', [params.characterLimit]);
  }
});

extend('contactType', {
  validate:exists,
  computesRequired: true,
  message: i18n.t('Validations.contactType')
});

extend('contactLastName', {
  validate: exists,
  computesRequired: true,
  message: i18n.t('Validations.contactLastName')
});

extend('requiredAttributes', {
  params: ['attributeValue'],
  validate: (value, { attributeValue }) => [value, attributeValue].every(exists),
  computesRequired: true,
  message: (value, { attributeValue }) =>  i18n.t('Validations.requiredAttributes', [attributeValue])
});

extend('contractItemValueRequired', {
  validate: exists,
  computesRequired: true,
  message: i18n.t('Validations.enterValue')
});

extend('holidayReasonRequired', {
  validate(value) {
    return value?.trim().length > 0 && value !== undefined;
  },
  computesRequired: true,
  message: i18n.t('Validations.required')
});
